<template>
    <div>
        <!-- Controles principais -->
        <div class="controls-section">
            <div class="d-flex justify-content-center mb-3">
                <div class="btn-group" role="group">
                    <button type="button" class="btn btn-outline-primary" :class="{ active: viewMode === 'chart' }"
                        @click="changeViewMode('chart')">
                        {{ $t('chart') }}
                    </button>
                    <button type="button" class="btn btn-outline-primary" :class="{ active: viewMode === 'table' }"
                        @click="changeViewMode('table')">
                        {{ $t('table') }}
                    </button>
                </div>
            </div>

            <div class="d-flex justify-content-center mb-2">
                <div class="btn-group" role="group">
                    <button type="button" class="btn btn-outline-secondary" :class="{ active: metric === 'all' }"
                        @click="metric = 'all'">
                        {{ $t('all') }}
                    </button>
                    <button type="button" class="btn btn-outline-secondary" :class="{ active: metric === 'serie' }"
                        @click="metric = 'serie'">
                        {{ $t('series') }}
                    </button>
                    <button type="button" class="btn btn-outline-secondary" :class="{ active: metric === 'repeticao' }"
                        @click="metric = 'repeticao'">
                        {{ $t('repetitions') }}
                    </button>
                    <button type="button" class="btn btn-outline-secondary" :class="{ active: metric === 'ton' }"
                        @click="metric = 'ton'">
                        {{ $t('TON') }}
                    </button>
                    <button type="button" class="btn btn-outline-secondary" :class="{ active: metric === 'pm' }"
                        @click="metric = 'pm'">
                        {{ $t('PM') }}
                    </button>
                </div>
            </div>

            <!-- Botão de Preview (aparece para todos os períodos) -->
            <div class="d-flex justify-content-center mb-3">
                <button type="button" class="btn btn-outline-info btn-sm" @click="abrirPreviewPDF('grupos')">
                    <i class="fas fa-eye"></i> {{ $t('viewPreview') }}
                </button>
            </div>
        </div>
        <!-- Visualização Gráfico -->
        <div v-if="viewMode === 'chart'" class="muscle-groups-chart">
            <div class="chart-header">
                <h2 class="chart-title">{{ $t('muscleGroupsStatistics') }}</h2>
            </div>
            <div v-if="hasAnyData">
                <div class="row">
                    <div v-for="type in chartTypesToShow" :key="type"
                        :class="['col-12', chartTypesToShow.length === 1 ? 'col-md-12' : 'col-md-6', 'mb-4']">
                        <h4 class="text-center mb-2">{{ chartTitles[type] }}</h4>
                        <div :class="['chart-container', { 'single-chart': chartTypesToShow.length === 1 }]"
                            v-if="chartDataByType(type).length" :data-type="type">
                            <div class="chart-wrapper">
                                <div class="y-axis">
                                    <div v-for="tick in yTicksByType(type)" :key="tick" class="y-tick">
                                        {{ formatValueType(tick, type) }}
                                    </div>
                                </div>
                                <div class="bars-container">
                                    <div v-for="(item, index) in chartDataByType(type)" :key="index" class="bar-group">
                                        <div class="bar-wrapper">
                                            <div class="bar-value-label"
                                                :style="{ bottom: getBarHeightType(item, type) + '%' }">
                                                {{ formatValueType(getMetricValue(item, type), type) }}
                                            </div>
                                            <div class="bar" :style="{
                                                height: getBarHeightType(item, type) + '%',
                                                backgroundColor: getBarColorType(type)
                                            }"
                                                :title="`${item.name}: ${formatValueType(getMetricValue(item, type), type)}`">
                                            </div>
                                        </div>
                                        <div class="bar-label">{{ item.name }}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="chart-legend">
                                <div class="legend-item">
                                    <div class="legend-color" :style="{ backgroundColor: getBarColorType(type) }"></div>
                                    <span>{{ chartTitles[type] }} (Valores realizados)</span>
                                </div>
                            </div>
                        </div>
                        <div v-else class="no-data">
                            <p>{{ $t('noDataAvailableForPeriod') }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="no-data-global">
                <p>{{ $t('selectPeriodAndStudent') }}</p>
            </div>
        </div>

        <!-- Visualização Tabela -->
        <div v-if="viewMode === 'table'" class="table-section">
            <div class="table-header">
                <h2>{{ $t('detailedDataMuscleGroups') }}</h2>
            </div>
            <div class="table-responsive">
                <table class="table table-bordered table-hover text-center">
                    <thead class="table-dark">
                        <tr>
                            <th>#</th>
                            <th>{{ $t('muscleGroupsColumn') }}</th>
                            <th>{{ $t('seriesRealized') }}</th>
                            <th>{{ $t('repetitionRealized') }}</th>
                            <th>{{ $t('tonnageKg') }}</th>
                            <th>{{ $t('averageWeightKg') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(group, idx) in processedData" :key="group.name" :class="getRowClass(idx)">
                            <td class="fw-bold">{{ idx + 1 }}</td>
                            <td class="text-start">{{ group.name }}</td>
                            <td>{{ group.serie }}</td>
                            <td>{{ group.repeticao }}</td>
                            <td>{{ formatValue(group.ton) }}</td>
                            <td>{{ formatValue(group.pm) }}</td>
                        </tr>
                    </tbody>
                    <tfoot v-if="processedData.length" class="table-secondary">
                        <tr>
                            <td colspan="2" class="text-end fw-bold">{{ $t('totals') }}:</td>
                            <td class="fw-bold">{{ getTotal('serie') }}</td>
                            <td class="fw-bold">{{ getTotal('repeticao') }}</td>
                            <td class="fw-bold">{{ formatValue(getTotal('ton')) }}</td>
                            <td class="fw-bold">{{ formatValue(getTotal('pm')) }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>

        <!-- Modal de preview do PDF -->
        <div v-if="showPreviewModal" class="modal-preview-overlay">
            <div class="modal-preview-content">
                <button class="close-btn" @click="fecharPreviewPDF">&times;</button>
                <h5 class="mb-3">{{ previewDocumentTitle || $t('pdfPreview') }}</h5>
                <div v-if="previewLoading" class="text-center my-4">
                    <span class="spinner-border spinner-border-sm"></span> {{ $t('generatingPreview') }}
                </div>
                <div v-else>
                    <div v-if="previewUrl" class="pdf-iframe-preview mb-4">
                        <iframe :src="previewUrl" width="100%" height="900"
                            style="min-width:600px; min-height:800px; border:1px solid #ccc; border-radius:8px; box-shadow:0 2px 8px #0001;"></iframe>
                    </div>
                    <div v-else class="text-center text-danger mb-4">
                        Não foi possível gerar o preview do PDF.
                    </div>
                </div>
                <div class="d-flex justify-content-center">
                    <button type="button" class="btn btn-success" :disabled="pdfLoading" @click="enviarPDFdoPreview">
                        <span v-if="pdfLoading" class="spinner-border spinner-border-sm me-2"></span>
                        {{ previewMode === 'resumo' ? 'Enviar Resumo' : 'Enviar PDF' }}
                    </button>
                    <button type="button" class="btn btn-secondary ms-2" @click="fecharPreviewPDF">
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import html2canvas from 'html2canvas'
import { useStore } from 'vuex'
import avaliacaoFisicaRepository from '@/services/api/avaliacaoFisicaRepository'
import kpiRepository from '@/services/api/kpiRepository'
import ResumoMesChart from '@/components/Charts/ResumoMesChart.vue'
import CaloricMonthChart from '@/components/Charts/CaloricMonthChart.vue'

export default {
    name: 'MuscleGroupsChart',
    components: {
        ResumoMesChart,
        CaloricMonthChart
    },
    props: {
        studentId: [String, Number],
        dia: [String, Number],
        month: [String, Number],
        year: [String, Number],
        period: [String],
        data: { type: Array, default: () => [] }
    },
    setup(props) {
        const store = useStore()
        // Obter o ID do plano de treino do store
        const trainingPlanId = computed(() => {
            return store.state?.training_plan ||
                store.state?.trainingPlan ||
                store.state?.trainingPlanId ||
                store.state?.user?.training_plan_id ||
                null
        })

        const viewMode = ref('chart')
        const pdfLoading = ref(false)
        const metric = ref('all')
        const resumoMes = ref(null)
        const resumoCalorico = ref(null)
        const showPreviewModal = ref(false)
        const previewUrl = ref('')
        const previewLoading = ref(false)
        const previewMode = ref('full')
        const previewDocumentTitle = ref('')

        const chartTypes = ['serie', 'repeticao', 'ton', 'pm']
        const chartTitles = {
            serie: 'Séries',
            repeticao: 'Repetições',
            ton: 'Ton',
            pm: 'PM'
        }
        const chartColors = {
            serie: '#ff9800',
            repeticao: '#ec407a',
            ton: '#d32f2f',
            pm: '#388e3c'
        }

        const processedData = computed(() => {
            if (!props.data?.length) return []
            return props.data.map(item => ({
                name: item.grupo || 'N/A',
                serie: parseInt(item.serie) || 0,
                repeticao: parseInt(item.repeticao) || 0,
                ton: parseFloat(item.ton) || 0,
                pm: parseFloat(item.pm) || 0
            }))
        })

        const hasAnyData = computed(() => processedData.value.length > 0)
        const chartTypesToShow = computed(() => {
            return metric.value === 'all' ? chartTypes : [metric.value]
        })

        const chartDataByType = (type) => {
            if (!processedData.value.length) return []
            return [...processedData.value]
                .sort((a, b) => getMetricValue(b, type) - getMetricValue(a, type))
                .slice(0, 10)
        }

        const maxValueForType = (type) => {
            const data = chartDataByType(type)
            if (!data.length) return 100
            const values = data.map(item => getMetricValue(item, type))
            return Math.ceil(Math.max(...values) * 1.2)
        }

        const yTicksByType = (type) => {
            const max = maxValueForType(type)
            const tickCount = 5
            const step = max / (tickCount - 1)
            return Array.from({ length: tickCount }, (_, i) =>
                Math.round(step * (tickCount - 1 - i))
            )
        }

        const getMetricValue = (item, type) => {
            switch (type) {
                case 'serie': return item.serie
                case 'repeticao': return item.repeticao
                case 'ton': return item.ton
                case 'pm': return item.pm
                default: return item.serie
            }
        }

        const getBarHeightType = (item, type) => {
            const value = getMetricValue(item, type)
            const max = maxValueForType(type)
            const percentage = max > 0 ? (value / max) * 100 : 0
            return Math.max(percentage, 2)
        }

        const getBarColorType = (type) => chartColors[type] || '#003366'

        const formatValueType = (value, type) => {
            if (typeof value !== 'number') return value
            if (['ton', 'pm'].includes(type)) return value.toFixed(1)
            return Math.round(value).toString()
        }

        const formatValue = (value) => {
            if (typeof value !== 'number') return value
            if (Math.abs(value) < 1000 && value % 1 !== 0) return value.toFixed(1)
            return Math.round(value).toString()
        }

        const getTotal = (type) => {
            const values = processedData.value.map(i => i[type])
            if (['ton', 'pm'].includes(type)) {
                const sum = values.reduce((a, b) => a + b, 0)
                return (sum / (values.length || 1)).toFixed(1)
            }
            return values.reduce((a, b) => a + b, 0)
        }

        const changeViewMode = (mode) => {
            viewMode.value = mode
        }

        const getRowClass = (index) => {
            return index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
        }

        const getMesNome = (mes) => {
            const meses = {
                '1': 'Janeiro', '2': 'Fevereiro', '3': 'Março',
                '4': 'Abril', '5': 'Maio', '6': 'Junho',
                '7': 'Julho', '8': 'Agosto', '9': 'Setembro',
                '10': 'Outubro', '11': 'Novembro', '12': 'Dezembro',
                '01': 'Janeiro', '02': 'Fevereiro', '03': 'Março',
                '04': 'Abril', '05': 'Maio', '06': 'Junho',
                '07': 'Julho', '08': 'Agosto', '09': 'Setembro'
            };
            return meses[mes] || mes;
        };

        // Buscar resumo do mês quando necessário
        const buscarResumoMes = async () => {
            if (props.period !== 'mes' || !props.studentId) {
                resumoMes.value = null
                resumoCalorico.value = null
                return
            }

            try {
                const res = await kpiRepository.TrainingResumeGet()
                if (res.data.success) {
                    const resumo = res.data.data
                    console.log('📊 Dados de resumo recebidos:', resumo)

                    // Verificar os detalhes_por_mes disponíveis
                    if (resumo.detalhes_por_mes) {
                        console.log('📅 Meses disponíveis:', Object.keys(resumo.detalhes_por_mes))
                    }

                    // Guardar o resumo completo com detalhes_por_mes para poder acessar qualquer mês
                    resumoMes.value = {
                        ...resumo,
                        detalhes_mes: null // Inicializar como null
                    }

                    // Se temos mês e ano selecionados, buscar detalhes específicos do mês
                    if (props.month && props.year && resumo.detalhes_por_mes) {
                        const key = `${props.year}-${String(props.month).padStart(2, '0')}`
                        console.log('🔑 Buscando dados para chave:', key)

                        resumoMes.value.detalhes_mes = resumo.detalhes_por_mes[key] || null

                        if (resumoMes.value.detalhes_mes) {
                            console.log('✅ Dados específicos do mês encontrados:', resumoMes.value.detalhes_mes)
                        } else {
                            console.warn('⚠️ Não foram encontrados dados para o mês/ano selecionado:', key)
                        }
                    }
                }
            } catch (error) {
                console.error('Erro ao buscar resumo:', error)
            }

            try {
                const resCal = await kpiRepository.CaloricExpenditureByMonthGet(
                    props.year,
                    props.month
                )
                if (resCal.data.success) {
                    resumoCalorico.value = resCal.data.data
                }
            } catch (error) {
                console.error('Erro ao buscar gasto calórico:', error)
            }
        }

        // Capturar gráficos como imagens
        const capturarGraficos = async (mode = 'full') => {
            try {
                const graficos = []

                // Função auxiliar para capturar elemento com espera
                const capturarElemento = async (el) => {
                    if (!el) return null
                    await nextTick() // garante que o DOM foi atualizado
                    await new Promise(r => setTimeout(r, 300)) // espera 300ms para o canvas renderizar
                    const canvas = await html2canvas(el, {
                        scale: 2,
                        useCORS: true,
                        backgroundColor: '#ffffff',
                        logging: false
                    })
                    return canvas.toDataURL('image/png')
                }

                // MODO RESUMO
                if (mode === 'resumo') {
                    // NÃO ESCONDER os dados textuais para o resumo
                    // Queremos que eles apareçam no PDF agora
                    const resumoEl = document.getElementById('resumo-mes-card')
                    const imgResumo = await capturarElemento(resumoEl)
                    if (imgResumo) graficos.push(imgResumo)

                    return graficos
                }

                // MODO GRUPOS
                if (mode === 'grupos') {
                    for (const type of chartTypesToShow.value) {
                        const el = document.querySelector(`.chart-container[data-type="${type}"]`)
                        const img = await capturarElemento(el)
                        if (img) graficos.push(img)
                    }
                    return graficos
                }

                // MODO FULL
                if (mode === 'full') {
                    // Resumo
                    const dadosTextuais = document.querySelector('.dados-textuais-resumo')
                    if (dadosTextuais) dadosTextuais.style.display = 'none'

                    const resumoEl = document.getElementById('resumo-mes-card')
                    const imgResumo = await capturarElemento(resumoEl)
                    if (imgResumo) graficos.push(imgResumo)

                    if (dadosTextuais) dadosTextuais.style.display = ''

                    // Gráficos de grupos
                    for (const type of chartTypesToShow.value) {
                        const el = document.querySelector(`.chart-container[data-type="${type}"]`)
                        const img = await capturarElemento(el)
                        if (img) graficos.push(img)
                    }

                    return graficos
                }

                return graficos
            } catch (error) {
                console.error('Erro ao capturar gráficos:', error)
                const dadosTextuais = document.querySelector('.dados-textuais-resumo')
                if (dadosTextuais) dadosTextuais.style.display = ''
                throw error
            }
        }

        // Gera o payload para envio
        const gerarPayload = async (mode = 'full') => {
            const graficos = await capturarGraficos(mode);

            if (!graficos || graficos.length === 0) {
                throw new Error('Nenhum gráfico capturado');
            }

            // Para o resumo, adicionar dados textuais explícitos para o backend
            if (mode === 'resumo') {
                // Obter dados específicos do mês selecionado
                let dadosMesEspecifico = null;
                if (resumoMes.value?.detalhes_por_mes && props.month && props.year) {
                    const key = `${props.year}-${String(props.month).padStart(2, '0')}`;
                    dadosMesEspecifico = resumoMes.value.detalhes_por_mes[key] || null;
                }

                // Log para depuração
                console.log('📤 Enviando dados para PDF:', {
                    mes_selecionado: props.month ? `${props.month}/${props.year}` : 'N/A',
                    dados_especificos: dadosMesEspecifico,
                    dados_gerais: {
                        total_treinos: resumoMes.value?.total_trainings,
                        executados: resumoMes.value?.total_trainings_executed,
                        contratados: resumoMes.value?.aulas_contratadas_mes
                    }
                });

                // Dados para envio ao backend
                const payload = {
                    graficos,
                    mode: 'resumo',
                    report_type: 'resumo',
                    title: 'Resumo do Mês',
                    resumo_mes: {
                        // Usar dados específicos do mês quando disponíveis
                        treinos_executados: dadosMesEspecifico?.executados ?? resumoMes.value?.total_trainings_executed ?? 0,
                        treinos_excedentes: dadosMesEspecifico?.excedentes ?? resumoMes.value?.total_trainings_excedentes ?? 0,
                        aulas_contratadas_mes: dadosMesEspecifico?.contratados ?? resumoMes.value?.aulas_contratadas_mes ?? 0,

                        // Outros dados gerais
                        total_treinos: resumoMes.value?.total_trainings || 0,
                        treinos_nao_executados: resumoMes.value?.total_trainings_not_executed || 0,
                        treinos_incompletos: resumoMes.value?.total_trainings_incomplete || 0,
                        gasto_calorico: resumoCalorico.value?.training_caloric || 0,
                        gasto_calorico_acumulado: resumoCalorico.value?.plan_accumulated || 0,
                        detalhes_mes: dadosMesEspecifico || null, // Usar diretamente os dados específicos
                    },
                    // Metadados explícitos sobre o mês específico
                    indicador_mes: {
                        nome: getMesNome(props.month),
                        numero: props.month,
                        ano: props.year,
                        eh_especifico: !!dadosMesEspecifico,
                        data_label: props.month && props.year ? `${getMesNome(props.month)}/${props.year}` : null
                    }
                };

                // Adicionar mes/ano
                if (props.month) payload.mes = props.month;
                if (props.year) payload.ano = props.year;

                return payload;
            }

            // Payload para outros modos
            if (mode === 'grupos') {
                const payload = {
                    graficos,
                    mode: 'grupos',
                    report_type: 'grupos',
                    title: 'Relatório de Grupos Musculares',
                    layout: 'grupos',
                    dados: processedData.value
                };

                if (props.month) payload.mes = props.month;
                if (props.year) payload.ano = props.year;
                if (props.period === 'dia' && props.dia) payload.dia = props.dia;

                return payload;
            }

            if (mode === 'full') {
                const payload = {
                    graficos,
                    mode: 'full',
                    report_type: 'full',
                    title: 'Relatório Completo',
                    dados: processedData.value
                };

                if (resumoMes.value) {
                    payload.resumo_mes = {
                        total_treinos: resumoMes.value.total_trainings || 0,
                        treinos_executados: resumoMes.value.total_trainings_executed || 0,
                        treinos_nao_executados: resumoMes.value.total_trainings_not_executed || 0,
                        treinos_incompletos: resumoMes.value.total_trainings_incomplete || 0,
                        treinos_excedentes: resumoMes.value.total_trainings_excedentes || 0,
                        aulas_contratadas_mes: resumoMes.value.aulas_contratadas_mes || 0,
                        gasto_calorico: resumoCalorico.value?.training_caloric || 0,
                        gasto_calorico_acumulado: resumoCalorico.value?.plan_accumulated || 0,
                        detalhes_mes: resumoMes.value.detalhes_mes || null
                    };
                }

                if (props.period === 'dia' && props.dia) payload.dia = props.dia;
                else if (props.period === 'mes') {
                    if (props.month) payload.mes = props.month;
                    if (props.year) payload.ano = props.year;
                }
                else if (props.period === 'ano' && props.year) payload.ano = props.year;

                return payload;
            }

            return { graficos, mode };
        };

        // Gerar e enviar PDF
        const gerarRelatorioPDF = async (mode = 'full') => {
            pdfLoading.value = true;
            try {
                const payload = await gerarPayload(mode);

                if (!payload || (!payload.graficos && !payload.resumo_mes && !payload.dados)) {
                    alert('Nenhum dado para enviar.');
                    return;
                }

                const headers = {
                    'student-manager': props.studentId || '',
                    'training-plan-manager': trainingPlanId.value || '1',
                    'content-type': 'application/json',
                    'x-report-type': mode,
                    'x-layout-type': mode === 'resumo' ? 'resumo_mensal' : 'padrao',
                    'x-disable-tables': mode === 'resumo' ? 'true' : 'false',
                    'x-disable-series-title': mode === 'resumo' ? 'true' : 'false'
                };

                const response = await avaliacaoFisicaRepository.MuscleGroupReportPDF(payload, { headers });

                if (response.data.success) {
                    alert(mode === 'resumo'
                        ? 'Resumo enviado com sucesso!'
                        : mode === 'grupos'
                            ? 'Relatório de grupos enviado com sucesso!'
                            : props.period === 'mes'
                                ? 'Relatório completo do mês enviado com sucesso!'
                                : 'Relatório enviado com sucesso!');
                } else {
                    alert(response.data.message || 'Erro desconhecido no envio');
                }
            } catch (e) {
                console.error('Erro completo:', e);
                alert(e.response?.data?.message || e.message || 'Erro ao gerar ou enviar o relatório.');
            } finally {
                pdfLoading.value = false;
            }
        };

        // Abrir preview do PDF
        const abrirPreviewPDF = async (mode = 'full') => {
            previewMode.value = mode;
            showPreviewModal.value = true;
            previewUrl.value = '';
            previewLoading.value = true;

            try {
                const payload = await gerarPayload(mode);
                previewDocumentTitle.value = payload.title || (mode === 'resumo' ? 'Resumo do Mês' : 'Pré-visualização do PDF');

                const headers = {
                    'student-manager': props.studentId || '',
                    'training-plan-manager': trainingPlanId.value || '1',
                    'content-type': 'application/json',
                    'x-report-type': mode,
                    'x-layout-type': mode === 'resumo' ? 'resumo_detalhado' : 'padrao',
                    'x-disable-tables': 'false',
                    'x-disable-series-title': mode === 'resumo' ? 'true' : 'false',
                    'x-include-text-data': mode === 'resumo' ? 'true' : 'false'
                };

                // Get the response with preview URL
                const response = await avaliacaoFisicaRepository.PreviewPdf(payload, { headers });

                // URL diferenciada para o resumo
                let url = response.data?.preview_url || '';
                if (url) {
                    const separator = url.includes('?') ? '&' : '?';
                    url = `${url}${separator}mode=${mode}&type=${mode}&docType=resumo_detalhado&disableTables=false&_=${Date.now()}`;

                    // Se for resumo, adiciona parâmetros específicos
                    if (mode === 'resumo') {
                        url += '&showTextData=true&includeTextValues=true';
                    }
                }

                previewUrl.value = url;
            } catch (e) {
                previewUrl.value = '';
                console.error('Erro ao gerar preview:', e);
            } finally {
                previewLoading.value = false;
            }
        };

        // Fechar preview
        const fecharPreviewPDF = () => {
            showPreviewModal.value = false;
            previewUrl.value = '';
            previewLoading.value = false;
        };

        // Métodos auxiliares para UI
        const enviarResumoPDF = async () => await gerarRelatorioPDF('resumo');
        const enviarGruposPDF = async () => await gerarRelatorioPDF('grupos');

        const enviarPDFdoPreview = async () => {
            await gerarRelatorioPDF(previewMode.value || 'full');
            showPreviewModal.value = false;
            previewUrl.value = '';
        };

        // Watch para atualizar resumo quando mudar período/aluno/mês
        watch([() => props.period, () => props.studentId, () => props.month, () => props.year], () => {
            buscarResumoMes();
        }, { immediate: true });

        // Retornar month e year explicitamente para template
        return {
            viewMode,
            pdfLoading,
            metric,
            resumoMes,
            resumoCalorico,
            processedData,
            hasAnyData,
            chartTypes,
            chartTitles,
            chartTypesToShow,
            chartDataByType,
            yTicksByType,
            getBarHeightType,
            getBarColorType,
            formatValueType,
            getMetricValue,
            formatValue,
            getTotal,
            changeViewMode,
            getRowClass,
            gerarRelatorioPDF,
            capturarGraficos,
            showPreviewModal,
            previewUrl,
            previewLoading,
            previewDocumentTitle,
            previewMode,
            abrirPreviewPDF,
            fecharPreviewPDF,
            enviarPDFdoPreview,
            enviarResumoPDF,
            enviarGruposPDF,
            trainingPlanId,
            month: computed(() => props.month),
            year: computed(() => props.year),
            getMesNome,
        };
    }
};
</script>

<style scoped>
.resumo-mes-section {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 10px;
    margin-bottom: 20px;
}

.resumo-mes-section h4 {
    color: #2c3e50;
    font-weight: 700;
}

.controls-section {
    margin-bottom: 30px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 10px;
}

.gap-2 {
    gap: 0.5rem;
}

.btn-group .btn {
    white-space: nowrap;
}

.btn-group .btn i {
    margin-right: 4px;
}

.muscle-groups-chart {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
}

.chart-header {
    text-align: center;
    margin-bottom: 20px;
}

.chart-title {
    font-size: clamp(1.1rem, 3vw, 1.4rem);
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 5px;
}

.chart-container {
    height: auto;
    min-height: 350px;
    position: relative;
    background: white;
    padding: 10px;
    overflow-x: auto;
}

.chart-wrapper {
    display: flex;
    height: 100%;
    min-height: 300px;
    gap: 10px;
}

.y-axis {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-right: 8px;
}

.y-tick {
    font-size: clamp(9px, 2.5vw, 12px);
    color: #666;
    text-align: right;
    display: flex;
    align-items: space-between;
    justify-content: flex-end;
    position: relative;
    height: calc(100% / 4);
}

.y-tick:last-child {
    height: 0;
    align-items: center;
}

.bars-container {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: flex-end;
    justify-content: space-around;
    padding: 0 5px 0 0;
    border-left: 2px solid #34495e;
    border-bottom: 2px solid #34495e;
    position: relative;
    background: linear-gradient(to top, #f9f9f9 0%, transparent 100%);
}

.bar-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 40px;
    max-width: none;
    flex: 1;
    position: relative;
    z-index: 2;
    margin: 0 2px;
}

.bar-wrapper {
    height: 250px;
    width: 100%;
    display: flex;
    align-items: flex-end;
    margin-bottom: 10px;
    position: relative;
    justify-content: center;
}

.bar {
    width: min(35px, 100%);
    min-height: 5px;
    border-radius: 4px 4px 0 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    cursor: pointer;
}

.bar:hover {
    opacity: 0.8;
    transform: scale(1.05);
}

.bar-value-label {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 6px;
    font-size: clamp(9px, 2.5vw, 11px);
    font-weight: 700;
    color: #2c3e50;
    background: rgba(255, 255, 255, 0.95);
    padding: 2px 4px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    white-space: nowrap;
    z-index: 3;
    border: 1px solid #e0e0e0;
}

.bar-label {
    font-size: clamp(9px, 2.5vw, 11px);
    color: #2c3e50;
    text-align: center;
    line-height: 1.2;
    max-width: 100%;
    word-wrap: break-word;
    height: auto;
    min-height: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    margin-bottom: 10px;
    overflow-wrap: break-word;
    hyphens: auto;
}

.chart-legend {
    margin-top: 8px;
    text-align: center;
}

.legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    background: #f8f9fa;
    border-radius: 20px;
    font-size: clamp(10px, 2.5vw, 12px);
}

.legend-color {
    width: 14px;
    height: 14px;
    border-radius: 3px;
}

.chart-container.single-chart .bars-container {
    justify-content: space-between;
    padding: 20px;
}

.chart-container.single-chart .bar-group {
    max-width: 120px;
    flex: 0 0 auto;
    margin: 0 8px;
}

.chart-container.single-chart .bar {
    width: min(60px, 8vw);
}

.table-section {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.table-header {
    text-align: center;
    margin-bottom: 20px;
}

.table-header h2 {
    font-size: clamp(1.1rem, 3vw, 1.4rem);
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 5px;
}

.table-hover tbody tr:hover {
    background-color: #f8f9fa;
}

.table-row-even {
    background-color: #fafafa;
}

.table-row-odd {
    background-color: white;
}

.no-data-global {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #7f8c8d;
    font-size: clamp(1rem, 3vw, 1.2rem);
    font-weight: 600;
    text-align: center;
}

.modal-preview-overlay {
    position: fixed;
    z-index: 2000;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-preview-content {
    background: #fff;
    border-radius: 10px;
    padding: 32px 24px 24px 24px;
    min-width: 60vw;
    max-width: 98vw;
    max-height: 98vh;
    overflow-y: auto;
    box-shadow: 0 8px 32px #0003;
    position: relative;
}

.close-btn {
    position: absolute;
    top: 10px;
    right: 16px;
    background: none;
    border: none;
    font-size: 2rem;
    color: #888;
    cursor: pointer;
    z-index: 10;
}

.pdf-iframe-preview {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto 16px auto;
    background: #f8f9fa;
    border-radius: 8px;
    overflow: hidden;
}

/* Estilos para destacar o indicador de mês */
.month-indicator {
    color: #3498db;
    font-weight: 700;
}

@media (max-width: 600px) {
    .modal-preview-content {
        padding: 12px 4px 16px 4px;
        min-width: 0;
    }
}

@media (max-width: 768px) {
    .resumo-mes-section {
        padding: 15px;
    }

    .controls-section {
        padding: 10px;
        margin-bottom: 20px;
    }

    .muscle-groups-chart {
        padding: 10px;
    }

    .chart-container {
        min-height: 300px;
        padding: 8px;
    }
}
</style>